"use client";

import { useTranslations } from "use-intl";

import { SparkleIcon } from "@/components/appIcons";
import type { OfferListSummary } from "@/components/marketplace/offer-list-summary";
import { OFFER_ROW_WIDE_COLUMNS } from "@/components/marketplace/offer-row";
import { Price } from "@/components/Price";
import { cn } from "@/lib/utils";

/** Count, price range and the work-level super-offer chip, then the wide-layout column labels. */
export function OfferListHeader({
  summary,
  condition = "new",
}: {
  summary: OfferListSummary;
  condition?: "new" | "used";
}) {
  const t = useTranslations("shared.affiliates");
  const { minTotal, maxTotal, currency } = summary;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-3 sm:px-5">
        <p className="text-sm font-bold text-foreground">
          {t("offerListCount", { count: summary.count })}
        </p>
        {minTotal !== null && maxTotal !== null && currency && (
          <p className="text-xs text-muted-foreground">
            {minTotal === maxTotal ? (
              <Price amount={minTotal} currency={currency} plain />
            ) : (
              t.rich("offerListRange", {
                min: () => <Price amount={minTotal} currency={currency} plain />,
                max: () => <Price amount={maxTotal} currency={currency} plain />,
              })
            )}
            {summary.count > 1 && <> · {t("offerListOrder")}</>}
          </p>
        )}
        {summary.hasSuperOffer && (
          <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/15 px-2.5 py-1 text-xs font-bold text-amber-700 dark:text-amber-300">
            <SparkleIcon aria-hidden="true" className="text-sm" />
            {t("superOffer")}
          </span>
        )}
      </div>
      {condition === "new" && (
        <div
          aria-hidden="true"
          className={cn(
            "hidden border-y border-border/60 bg-background/40 py-2 pl-3 pr-5 text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground @4xl:grid @4xl:gap-x-4",
            OFFER_ROW_WIDE_COLUMNS,
          )}
        >
          <span className="col-start-3">{t("offerColumnListing")}</span>
          <span>{t("offerColumnHistory")}</span>
          <span className="text-right">{t("offerColumnPrice")}</span>
          <span className="text-right">{t("offerColumnStore")}</span>
        </div>
      )}
    </div>
  );
}
